import { getApolloClient } from '../lib/getApolloClient';
import { handleError } from '../util/utils';
import { GET_TENANT_BY_SLUG } from './tenant.rest';

export interface TenantInfo {
  tenantName: string;
  environment: string;
  fullDomain: string;
}

export interface TenantResolutionResult {
  tenantId: string;
  tenantName: string;
  environment: string;
  success: boolean;
  error?: string;
  logoUrl?: string | null;
  slug?: string | null;
  enableClientPortal?: boolean;
}

// Supported base domains
const SUPPORTED_DOMAINS = ['insta-agency.com', 'zrec.io'];

const getBaseDomain = (hostname: string): string | null => {
  for (const domain of SUPPORTED_DOMAINS) {
    if (hostname.endsWith(domain)) {
      return domain;
    }
  }
  return null;
};

const extractTenantFromDomain = (): TenantInfo | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const hostname = window.location.hostname;

  // Handle localhost for development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check for tenant context to use as tenant name
    const tenantContext = process.env.VITE_TENANT_SLUG;
    const tenantName = tenantContext;

    if (!tenantName) {
      throw new Error('Tenant slug is not set');
    }

    return {
      tenantName: tenantName,
      environment: 'dev',
      fullDomain: hostname,
    };
  }

  // Domain pattern: tenant.{env}-app.{base-domain}
  // - Production: tenant.app.zrec.io or tenant.app.insta-agency.com
  // - Staging: tenant.staging-app.zrec.io
  // - Dev: tenant.dev-app.zrec.io
  const baseDomain = getBaseDomain(hostname);

  if (!baseDomain) {
    console.error(
      `Invalid domain format. Expected: tenant.{env}-app.{${SUPPORTED_DOMAINS.join('|')}}`,
    );
    return null;
  }

  // Calculate the number of base domain parts (e.g., 'zrec.io' = 3, 'insta-agency.com' = 2)
  const baseDomainParts = baseDomain.split('.').length;
  const domainParts = hostname.split('.');
  const prefixParts = domainParts.length - baseDomainParts;

  // Check if it's a base domain without tenant (e.g., app.zrec.io or staging-app.zrec.io)
  if (prefixParts === 1) {
    const envAppPart = domainParts[0];
    if (envAppPart === 'app' || envAppPart.endsWith('-app')) {
      // This is a base domain without tenant - return null to indicate no tenant
      return null;
    }
    console.error(
      `Invalid domain format. Expected: tenant.{env}-app.${baseDomain}`,
    );
    return null;
  }

  // Standard pattern: tenant.{env}-app.{base-domain}
  if (prefixParts === 2) {
    const tenantName = domainParts[0];
    const envAppPart = domainParts[1];

    // Validate format: must be 'app' or end with '-app'
    if (envAppPart !== 'app' && !envAppPart.endsWith('-app')) {
      console.error(
        'Invalid environment format. Expected: app or {env}-app',
      );
      return null;
    }

    // Extract environment: 'app' -> 'prod', 'staging-app' -> 'staging', 'dev-app' -> 'dev'
    const environment = envAppPart === 'app' ? 'prod' : envAppPart.replace('-app', '');

    return {
      tenantName,
      environment,
      fullDomain: hostname,
    };
  }

  console.error(
    `Invalid domain format. Expected: tenant.{env}-app.${baseDomain}`,
  );
  return null;
};

const resolveTenantId = async (
  tenantName: string,
  environment: string,
): Promise<TenantResolutionResult> => {
  // For localhost development, still fetch from API but use VITE_TENANT_CONTEXT as fallback tenant name
  if (
    environment === 'dev' &&
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1')
  ) {
    const tenantContext = process.env.VITE_TENANT_SLUG;
    if (tenantContext && tenantName === 'test-tenant') {
      // Use the tenant context as the tenant name for local development
      tenantName = tenantContext;
    }
  }

  try {
    const result = await getApolloClient(true).query({
      query: GET_TENANT_BY_SLUG,
      variables: {
        slug: tenantName,
      },
    });

    if (result.data.tenantBySlug && result.data.tenantBySlug.id) {
      return {
        tenantId: result.data.tenantBySlug.id,
        tenantName: result.data.tenantBySlug.name,
        slug: tenantName,
        environment,
        success: true,
        logoUrl: result.data.tenantBySlug.logoUrl || null,
        enableClientPortal:
          result.data.tenantBySlug.enableClientPortal ?? false,
      };
    } else {
      throw new Error('Invalid tenant data received');
    }
  } catch (error) {
    handleError(error, 'Error in resolveTenantId');
    return {
      tenantId: '',
      tenantName,
      environment,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

const getCurrentTenantInfo = async (): Promise<{
  tenantInfo: TenantInfo | null;
  resolution: TenantResolutionResult | null;
}> => {
  const tenantInfo = extractTenantFromDomain();

  if (!tenantInfo) {
    return { tenantInfo: null, resolution: null };
  }

  const resolution = await resolveTenantId(
    tenantInfo.tenantName,
    tenantInfo.environment,
  );
  return { tenantInfo, resolution };
};

export const tenantActions = {
  getCurrentTenantInfo,
  extractTenantFromDomain,
  resolveTenantId,
};
